'use client';

import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

interface LandingButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'accent' | 'navy';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  onClick?: () => void;
  showArrow?: boolean;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

const variantStyles = {
  primary:
    'bg-neutral-900 text-white border-2 border-neutral-900 hover:bg-neutral-800 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]',
  secondary:
    'bg-white text-neutral-900 border-2 border-neutral-900 hover:bg-neutral-50 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]',
  outline: 'bg-transparent text-neutral-900 border-2 border-neutral-900 hover:bg-neutral-50',
  ghost: 'bg-transparent text-neutral-900 hover:bg-neutral-100',
  accent:
    'bg-teal-500 text-white border-2 border-teal-500 hover:bg-teal-400 hover:border-teal-400 shadow-[3px_3px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[1px] hover:translate-y-[1px]',
  navy: 'bg-navy-900 text-white hover:bg-navy-800',
};

const sizeStyles = {
  sm: 'px-4 py-2 text-sm font-semibold rounded-lg',
  md: 'px-6 py-3 text-base font-bold rounded-xl',
  lg: 'px-8 py-4 text-lg font-bold rounded-xl',
};

export function LandingButton({
  children,
  variant = 'primary',
  size = 'md',
  href,
  onClick,
  showArrow = false,
  className = '',
  disabled = false,
  loading = false,
}: LandingButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center gap-2 transition-all duration-200 font-sans tracking-tight disabled:opacity-50 disabled:cursor-not-allowed';

  const combinedStyles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  const content = (
    <>
      {loading ? (
        <span className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
      ) : (
        children
      )}
      {showArrow && !loading && (
        <ArrowRight
          size={size === 'lg' ? 20 : size === 'md' ? 18 : 16}
          className="group-hover:translate-x-1 transition-transform"
        />
      )}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={combinedStyles}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled || loading} className={combinedStyles}>
      {content}
    </button>
  );
}
